/**
 * ThemeContext — central source for the user's theme preference.
 *
 * Hierarchy of truth:
 *   1. Server (`me.theme` from /auth/me) — authoritative once authenticated.
 *   2. localStorage — first-paint fallback, also keeps anonymous visitors
 *      consistent across reloads.
 *   3. `prefers-color-scheme` media query — when the resolved preference is
 *      `system`.
 *
 * Default for new visitors: `dark` (founder-locked per Batch 27f).
 *
 * On change: PATCH /users/me { theme } (best-effort) + write localStorage +
 * toggle `.dark` on <html>.
 *
 * The pure context object lives in `./themeContextValue.ts` and the
 * `useTheme` hook in `./useTheme.ts` so this file exports components only
 * (fast-refresh constraint).
 *
 * UI-v2 Phase 0: added `data-brand` seam. ThemeProvider writes
 * `data-brand="<id>"` on <html> alongside `data-theme`, enabling the
 * [data-brand] CSS override blocks in tokens.css. Default = "certain".
 * Also sets document.title from brand config at boot.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  applyTheme,
  readStoredPreference,
  resolvePreference,
  THEME_STORAGE_KEY,
  type EffectiveTheme,
  type ThemePreference,
} from "@/lib/theme";
import { useMe } from "@/hooks/queries/auth/useMe";
import { useUpdateProfile } from "@/hooks/queries/auth/useProfile";
import { brand } from "@/config/brand";
import { ThemeContext, type ThemeContextValue } from "./themeContextValue";

/** Write the brand id as a data-brand attribute on <html>. */
function applyBrand(brandId: string): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-brand", brandId);
}

export function ThemeProvider({ children }: { children: ReactNode }): JSX.Element {
  const [preference, setPreferenceState] = useState<ThemePreference>(() =>
    readStoredPreference(),
  );
  const [effective, setEffective] = useState<EffectiveTheme>(() =>
    resolvePreference(readStoredPreference()),
  );

  const me = useMe();
  const updateProfile = useUpdateProfile();

  // Apply brand seam once at mount (Phase 0: always "certain").
  // Sets data-brand on <html> and document.title from brand config.
  useEffect(() => {
    applyBrand(brand.themeAttr);
    if (typeof document !== "undefined") {
      document.title = brand.name;
    }
  }, []);

  // 1) Hydrate from server preference once /auth/me arrives. Only overrides
  //    local state when the server value differs from what we already have
  //    (avoids the rare race where a freshly-set preference is round-trip
  //    overwritten by an in-flight stale me.data fetch).
  useEffect(() => {
    const serverTheme = me.data?.theme;
    if (
      serverTheme === "light" ||
      serverTheme === "dark" ||
      serverTheme === "system"
    ) {
      if (serverTheme !== preference) {
        setPreferenceState(serverTheme);
      }
    }
    // Intentionally NOT depending on `preference` — we want this to fire only
    // when the server value changes (otherwise it loops with setPreference).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.data?.theme]);

  // 2) Whenever preference changes, persist + apply.
  useEffect(() => {
    const next = resolvePreference(preference);
    setEffective(next);
    applyTheme(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(THEME_STORAGE_KEY, preference);
    }
  }, [preference]);

  // 3) When preference is `system`, follow the OS scheme live.
  useEffect(() => {
    if (preference !== "system") return;
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      const next: EffectiveTheme = mq.matches ? "dark" : "light";
      setEffective(next);
      applyTheme(next);
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [preference]);

  const setPreference = useCallback(
    (pref: ThemePreference) => {
      setPreferenceState(pref);
      // Best-effort server sync; ignore failure (local state still wins for
      // the current session).
      if (me.data) {
        updateProfile.mutate({ theme: pref });
      }
    },
    [me.data, updateProfile],
  );

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, effective, setPreference, brand: brand.id }),
    [preference, effective, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
